import type { PlanogramItem, UnrangedPlanogramItem } from "@CommonTypes/merchflow/pog/item";
import type { Planogram } from "@CommonTypes/merchflow/pog/planogram";
import type { PlanogramShelf } from "@CommonTypes/merchflow/pog/shelf";
import type { Pog } from "@CommonTypes/merchflow/pog/pog";
import type { BaseProduct } from "@CommonTypes/merchflow/pog/product";
import type { PlanogramBay } from "@CommonTypes/merchflow/pog/bay";
import type { DragBounds, Snappable } from "./types";

/**
 * A rack board's `standsOn` while it names a board of `bay` (a rack stands in
 * its host's bay), else null. The backend rewrites a kept one under the host's
 * new id on save.
 */
export const standsOnWithinBay = (standsOn: string | null, bay: PlanogramBay): string | null =>
  standsOn !== null && bay.shelves.some((shelf) => shelf.uniqueId === standsOn) ? standsOn : null;

export const getNewShelfNotchNo = ({
  bayHeight,
  cursorY,
  maxNotch,
  notchSpacing,
  scaleY,
}: {
  bayHeight: number;
  cursorY: number;
  maxNotch: number;
  notchSpacing: number;
  scaleY: number;
}) => Math.min(maxNotch, Math.round((bayHeight - cursorY) / scaleY / notchSpacing + 1)) - 1;

export const getOuterBounds = (planogram: Planogram) => {
  return {
    xLeft: 0,
    xRight: planogram.getWidth(),
    yBottom: 0,
    yTop: planogram.getHeight(),
  } satisfies DragBounds;
};

export const getSnappableFixtures = (planogram: Planogram) => {
  const snappables: Snappable[] = [];

  planogram.bays.forEach((bay) => {
    snappables.push({
      xLeft: bay.getXLeft(),
      xRight: bay.getXRight(),
      yBottom: 0,
      yTop: bay.height,
    });

    bay.shelves.forEach((shelf) => {
      snappables.push({
        xLeft: shelf.getXLeft(),
        xRight: shelf.getXRight(),
        yBottom: shelf.getYBottom(),
        yTop: shelf.getYTop(),
      });
    });
  });

  return snappables;
};

export const removeItemFromPlanogramAndUnrangedItems = ({
  pog,
  itemId,
}: {
  pog: Pog;
  itemId: string;
}) => {
  // Remove old item from unranged items.
  const indexAtUnrangedItems = pog.unrangedItems.findIndex((item) => item.uniqueId === itemId);
  if (indexAtUnrangedItems !== -1) {
    pog.unrangedItems.splice(indexAtUnrangedItems, 1);
  }

  // Remove old item from planogram.
  for (const bay of pog.planogram.bays) {
    for (const shelf of bay.shelves) {
      const oldItemIndex = shelf.items.findIndex((item) => item.uniqueId === itemId);
      if (oldItemIndex !== -1) {
        shelf.items.splice(oldItemIndex, 1);
      }
    }
  }
};

export const findPogItem = (pog: Pog, uniqueId: string) => {
  for (const bay of pog.planogram.bays) {
    for (const shelf of bay.shelves) {
      for (const item of shelf.items) {
        if (item.uniqueId === uniqueId) {
          return item;
        }
      }
    }
  }

  return null;
};

export const findPogShelf = ({ pog, shelfId }: { pog: Pog; shelfId: string }) => {
  for (const bay of pog.planogram.bays) {
    for (const shelf of bay.shelves) {
      if (shelf.uniqueId === shelfId) {
        return shelf;
      }
    }
  }

  return null;
};

export const areShelvesInSameMergedGroup = (shelves: PlanogramShelf[]) => {
  const shelfGroupId = shelves[0]?.getFirstShelf().uniqueId;

  return shelves.every((shelf) => shelf.getFirstShelf().uniqueId === shelfGroupId);
};

export const resolveDraggedPlanogramItems = (pog: Pog, itemIds: string[]): PlanogramItem[] => {
  const draggedItemIds = new Set(itemIds);

  return pog.planogram.getAllItems().filter((item) => draggedItemIds.has(item.uniqueId));
};

export const resolveDraggedItems = (
  pog: Pog,
  itemIds: string[],
): (PlanogramItem | UnrangedPlanogramItem)[] => {
  const draggedItemIds = new Set(itemIds);

  return [
    ...pog.planogram.getAllItems().filter((item) => draggedItemIds.has(item.uniqueId)),
    ...pog.unrangedItems.filter((item) => draggedItemIds.has(item.uniqueId)),
  ];
};

export const getFlattenedShelves = (pog: Pog): PlanogramShelf[] =>
  pog.planogram.bays.reduce<PlanogramShelf[]>((shelves, bay) => [...shelves, ...bay.shelves], []);

export interface StackOverClearance {
  item: PlanogramItem;
  clearance: number;
  maxRows: number;
}

/**
 * Shelf items whose stack plus y-caps stands taller than the room above
 * them. The room is read where the stack stands (`getClearanceAt` over its x,
 * which on a merged run may be a neighbouring bay): a board above that is not
 * live over the stack, such as a rack's tier beside it, does not bound it.
 */
export const findStacksOverClearance = (pog: Pog): StackOverClearance[] =>
  getFlattenedShelves(pog).flatMap((shelf) =>
    shelf.items.flatMap((item) => {
      const { xLeft, xRight } = item.getCoordinates();
      const clearance = shelf.getClearanceAt(xLeft, xRight);
      return item.getBlockHeight() > clearance + 1e-6
        ? [{ item, clearance, maxRows: item.getMaxRowsForClearance(clearance) }]
        : [];
    }),
  );

/** The stacks over clearance in `next` that were not over it in `prev`. */
export const findNewStacksOverClearance = (prev: Pog | null, next: Pog | null) => {
  if (!next) return [];
  const before = new Set(
    prev ? findStacksOverClearance(prev).map(({ item }) => item.uniqueId) : [],
  );
  return findStacksOverClearance(next).filter(({ item }) => !before.has(item.uniqueId));
};

export const resolvePlanogramShelves = (pog: Pog, shelfIds: string[]): PlanogramShelf[] =>
  shelfIds.flatMap((shelfId) => {
    const shelf = pog.getShelf(shelfId);
    return shelf ? [shelf] : [];
  });

// Range-selects every shelf/hangcell between the current selection's anchor (its first
// entry) and the newly shift-clicked shelf, ordered by bay then notch position.
export const getShelfRangeSelection = (
  pog: Pog,
  currentSelection: string[],
  clickedShelf: PlanogramShelf,
): string[] => {
  const anchorId = currentSelection[0];

  if (!anchorId) {
    return [clickedShelf.uniqueId];
  }

  if (anchorId === clickedShelf.uniqueId) {
    return [anchorId];
  }

  const flattenedShelves = getFlattenedShelves(pog);
  const anchorIndex = flattenedShelves.findIndex((shelf) => shelf.uniqueId === anchorId);
  const clickedIndex = flattenedShelves.findIndex(
    (shelf) => shelf.uniqueId === clickedShelf.uniqueId,
  );

  if (anchorIndex === -1 || clickedIndex === -1) {
    return [clickedShelf.uniqueId];
  }

  const [start, end] =
    anchorIndex < clickedIndex ? [anchorIndex, clickedIndex] : [clickedIndex, anchorIndex];

  const slicedIds = flattenedShelves.slice(start, end + 1).map((shelf) => shelf.uniqueId);
  const remainingIds = slicedIds.filter((shelfId) => shelfId !== anchorId);

  return [anchorId, ...remainingIds];
};

// Ctrl/Cmd-toggles a single shelf/hangcell in or out of the current selection without touching
// the others, so only the explicitly clicked fixtures end up selected (unlike the shift
// range-select, which also grabs everything in between). A newly added shelf becomes the anchor
// (first entry) so a following shift-click ranges from the last ctrl-clicked shelf.
export const getShelfToggleSelection = (
  currentSelection: string[],
  clickedShelf: Pick<PlanogramShelf, "uniqueId">,
): string[] => {
  const clickedId = clickedShelf.uniqueId;

  if (currentSelection.includes(clickedId)) {
    return currentSelection.filter((shelfId) => shelfId !== clickedId);
  }

  return [clickedId, ...currentSelection];
};

export interface ShelfSelectionModifier {
  shiftKey?: boolean;
  ctrlKey?: boolean;
  metaKey?: boolean;
}

export interface ShelfSelectionUpdateResult {
  nextSelectedShelfIds: string[];
  nextSelectedShelf: PlanogramShelf | null;
  nextTab: "ADD_EDIT_FIXTURE" | "FIXTURES_DETAILS";
}

// Calculates the updated fixture selection IDs, selected shelf entity, and panel tab route
// for pointer clicks (single-select, Shift range-select, or Ctrl/Cmd toggle-select).
export const calculateShelfSelectionUpdate = (
  pog: Pog | null,
  currentSelection: string[],
  clickedShelf: PlanogramShelf,
  modifier: ShelfSelectionModifier,
): ShelfSelectionUpdateResult => {
  if (modifier.shiftKey) {
    const range = pog
      ? getShelfRangeSelection(pog, currentSelection, clickedShelf)
      : [clickedShelf.uniqueId];
    return {
      nextSelectedShelfIds: range,
      nextSelectedShelf: range.length === 1 && pog ? pog.getShelf(range[0]) : null,
      nextTab: range.length > 1 ? "FIXTURES_DETAILS" : "ADD_EDIT_FIXTURE",
    };
  }

  if (modifier.ctrlKey || modifier.metaKey) {
    const toggled = getShelfToggleSelection(currentSelection, clickedShelf);
    return {
      nextSelectedShelfIds: toggled,
      nextSelectedShelf: toggled.length === 1 && pog ? pog.getShelf(toggled[0]) : null,
      nextTab: toggled.length === 1 ? "ADD_EDIT_FIXTURE" : "FIXTURES_DETAILS",
    };
  }

  return {
    nextSelectedShelfIds: [clickedShelf.uniqueId],
    nextSelectedShelf: clickedShelf,
    nextTab: "ADD_EDIT_FIXTURE",
  };
};

// Range-selected shelves form a contiguous notch run per bay, so when moving a multi-selection
// up/down only the highest ("top") or lowest ("bottom") notch shelf per bay can possibly collide
// with a shelf outside the selection — every other member's neighbor is also in the selection and
// moves along with it.
export const getBoundaryShelvesPerBay = (
  shelves: PlanogramShelf[],
  extreme: "top" | "bottom",
): PlanogramShelf[] => {
  const boundaryByBay = new Map<string, PlanogramShelf>();

  for (const shelf of shelves) {
    const current = boundaryByBay.get(shelf.bay.uniqueId);
    const isMoreExtreme =
      !current ||
      (extreme === "top"
        ? shelf.getNotchNo() > current.getNotchNo()
        : shelf.getNotchNo() < current.getNotchNo());

    if (isMoreExtreme) {
      boundaryByBay.set(shelf.bay.uniqueId, shelf);
    }
  }

  return [...boundaryByBay.values()];
};

export const isPlanogramItemMatching = (
  item: Pick<BaseProduct, "name" | "productCode">,
  search: string,
) => {
  return (
    search === "" ||
    item.name.toLowerCase().includes(search.toLowerCase()) ||
    item.productCode.toLowerCase().includes(search.toLowerCase())
  );
};
